
import Vue from "vue";
import Action, { ListAction, ListIncidentAction, ActionType, ActionStatus, ActionUser } from "@/features/Actions/Action";
import ActionDetailsModal from "@/features/Actions/ActionDetailsModal.vue";
import { ActionResponse } from "@/features/Actions/ActionService";
import { DataTableOptions, PagingParameter } from "@/shared/models/PagingParameter";
import AppError from "@/shared/components/AppError.vue";
import { formatDateWithTimestamp } from "@/shared/utils/common_utils";
import PlatformDocumentMetadata from "@/features/PlatformDocuments/PlatformDocumentMetadata";
import FileList, { FileListDocumentType } from "@/features/Documents/FileList.vue";

export default Vue.extend({
    name: "ActionList",
    components: {
        ActionDetailsModal,
        AppError,
        FileList,
    },
    props: {
        parentID: {
            type: String,
            required: false,
            default: "",
        },
        actionType: {
            type: Number as () => ActionType,
            required: true,
        },
    },
    data: () => ({
        ActionStatus: ActionStatus,
        headers: [
            {
                text: "Created",
                value: "createdTimestampUTC",
                sortable: true,
                align: "start",
            },
            {
                text: "Description",
                value: "description",
                sortable: true,
                align: "start",
            },
            {
                text: "User(s)",
                value: "users",
                sortable: true,
                align: "start",
            },
            {
                text: "Files",
                value: "files",
                sortable: false,
                align: "start",
            },
            {
                text: "Status",
                value: "status",
                sortable: true,
                align: "start",
            },
            {
                text: "",
                value: "id",
                sortable: false,
                align: "center",
            },
            {
                text: "",
                value: "actions",
                sortable: false,
                align: "center",
            },
        ],
        newAction: null as ListAction | null,
        modalAction: null as ListAction | null,
        showActionModal: false,
        actions: [] as ListAction[],
        totalActions: 0,
        loading: true,
        actionListOptions: {
            sortBy: ["id"],
            sortDesc: [false],
        } as DataTableOptions,
        errorMessage: "",
        showError: false,
        fileListDocumentTypes: FileListDocumentType,
    }),
    watch: {
        actionListOptions: {
            handler() {
                this.getActions();
            },
            deep: true,
        },
        supplierID: {
            handler() {
                this.getActions();
            },
        },
    },
    mounted() {
        this.actionListOptions.sortBy = [(this.$route.query.sortActionsBy as string) || "id"];
        this.actionListOptions.sortDesc = [(this.$route.query.sortActionsDesc as string) === "true" || false];
        this.actionListOptions.itemsPerPage = Number(this.$route.query.actionLimit) || 10;
        this.actionListOptions.page = Number(this.$route.query.actionPage) || 1;

        // Create the new action to pass to the modal based on the type
        switch (this.actionType) {
            case ActionType.ComplianceIncidentAction:
                this.newAction = new ListIncidentAction();
                this.newAction.actionType = ActionType.ComplianceIncidentAction;
                this.newAction.id = null;
                (this.newAction as ListIncidentAction).incidentID = this.parentID;
                break;
            default:
        }
    },
    created: function () {
        window.addEventListener("popstate", this.onBrowserNav); // Enables browser forward/back without full page reload
    },
    destroyed: function () {
        window.removeEventListener("popstate", this.onBrowserNav);
    },
    methods: {
        formatDateWithTimestampForTable(assessmentDate: string): string {
            const date = new Date(assessmentDate);
            return formatDateWithTimestamp(date);
        },
        formatUsersForTable(users: ActionUser[]): string {
            return users.map(u => u.name).join(", ");
        },
        formatFilesForTable(files: PlatformDocumentMetadata[]): string {
            return files.map(f => f.name).join(", ");
        },
        async onBrowserNav(event: PopStateEvent) {
            // Restore state
            this.actionListOptions = {
                sortBy: [event.state.sortActionsBy as string],
                sortDesc: [event.state.sortActionsDesc as boolean],
                itemsPerPage: event.state.actionLimit as number,
                page: event.state.actionPage as number,
            } as DataTableOptions;
        },
        async getActions(): Promise<void> {
            try {
                if (this.parentID) {
                    this.loading = true;
                    const pagingParam = new PagingParameter(this.actionListOptions);
                    let query = {};
                    switch (this.actionType) {
                        case ActionType.ComplianceIncidentAction:
                            query = {
                                ...pagingParam,
                                type: this.actionType,
                                incidentID: this.parentID,
                            };
                            break;
                        default:
                            query = {
                                ...pagingParam,
                                type: this.actionType,
                            };
                            break;
                    }

                    const response = (await this.$actionService.getActions(query)) as ActionResponse;
                    this.actions = response.actions as ListAction[];
                    this.totalActions = response.totalActions;
                }
            } catch (e) {
                this.errorMessage = "Failed to fetch supplier action data";
                this.showError = true;
            } finally {
                this.loading = false;
            }
        },
        actionSaved() {
            this.getActions();
        },
        handleChangeActionTableOptions(newOptions: DataTableOptions) {
            // Only update if the options were actually changed
            // This works in all cases except when browsing back from a different page,
            // we won't be able to go forward due to overwriting when pushing.
            // Potential option going forward: store in local browser history
            if (
                newOptions.sortBy[0] != this.actionListOptions.sortBy[0] ||
                newOptions.sortDesc[0] != this.actionListOptions.sortDesc[0] ||
                newOptions.itemsPerPage != this.actionListOptions.itemsPerPage ||
                newOptions.page != this.actionListOptions.page
            ) {
                const state = {
                    sortActionsBy: String(this.actionListOptions.sortBy),
                    sortActionsDesc: String(this.actionListOptions.sortDesc),
                    actionLimit: this.actionListOptions.itemsPerPage,
                    actionPage: this.actionListOptions.page,
                };

                const stringQueryParams = new URLSearchParams({
                    sortActionsBy: String(this.actionListOptions.sortBy),
                    sortActionsDesc: String(this.actionListOptions.sortDesc),
                    actionLimit: String(this.actionListOptions.itemsPerPage),
                    actionPage: String(this.actionListOptions.page),
                });

                history.pushState(state, "", window.location.href.split("?")[0] + "?" + new URLSearchParams(stringQueryParams).toString());

                this.actionListOptions = newOptions;
            }
        },
        async acknowledgeAction(id: string): Promise<void> {
            try {
                await this.$actionService.acknowledgeAction(id);
                this.getActions();
            } catch (e) {
                this.errorMessage = "Failed to acknowledge action.";
                this.showError = true;
            }
        },
        async completeAction(id: string): Promise<void> {
            try {
                await this.$actionService.completeAction(id);
                this.getActions();
            } catch (e) {
                this.errorMessage = "Failed to complete action.";
                this.showError = true;
            }
        },
        addAction(): void {
            this.modalAction = this.newAction;
            this.openDialog();
        },
        editAction(action: ListAction): void {
            this.modalAction = action;
            this.openDialog();
        },
        openDialog() {
            this.showActionModal = true;
        },
        canCreateAction(): boolean {
            return this.$actionAuthorizationService.canCreateAction();
        },
        closeDialog() {
            this.showActionModal = false;
        },
        async deleteAction(id: string): Promise<void> {
            try {
                await this.$actionService.deleteAction(id);
            } catch (e) {
                this.errorMessage = "Failed to delete action.";
                this.showError = true;
            } finally {
                this.getActions();
            }
        },
        canEditAction(action: Action): boolean {
            return this.$actionAuthorizationService.canEditAction(action);
        },
        canDeleteAction(action: Action): boolean {
            return this.$actionAuthorizationService.canDeleteAction(action);
        },
        canAcknowledgeAction(action: Action): boolean {
            return this.$actionAuthorizationService.canAcknowledgeAction(action);
        },
        canCompleteAction(action: Action): boolean {
            return this.$actionAuthorizationService.canCompleteAction(action);
        },
    },
});
